import { useCallback, useRef, useState } from "react";
import type { ScheduleConflictItem, ScheduleWorkingHoursViolation } from "@myapp/contract";
import { parseScheduleRejection } from "../lib/errors/parseScheduleRejection";
import type { ScheduleOverrides } from "../lib/schedule/overrides";

/**
 * The 409 half of any form that reserves time — the appointment and task upsert modals today.
 *
 * The hook owns *which dialog is open* and *what to resend*; it never submits anything itself.
 * The caller hands it a closure that repeats the rejected request with one more override, which
 * is what lets a two-call submit (task: update, then schedule) decide for itself what a retry
 * means.
 */

/** Repeats the rejected request with the accumulated overrides. */
export type ScheduleRetryAttempt = (overrides: ScheduleOverrides) => Promise<void>;

export interface ScheduleConflictFlow {
  conflicts: ScheduleConflictItem[] | null;
  blockers: ScheduleConflictItem[] | null;
  violations: ScheduleWorkingHoursViolation[] | null;
  /**
   * Feed a caught error in. Returns `true` when it was one of the three 409s and a dialog is now
   * open — the caller should stop. Returns `false` for everything else, which the caller shows
   * inline as it always did.
   */
  handle: (
    error: unknown,
    retry: { overrides: ScheduleOverrides; attempt: ScheduleRetryAttempt },
  ) => boolean;
  confirmConflict: () => Promise<void>;
  confirmWorkingHours: () => Promise<void>;
  /** Cancel: closes the two overridable dialogs and drops the stashed retry. */
  dismiss: () => void;
  closeBlockers: () => void;
  /** Back to a clean slate — call it when the owning modal opens. */
  reset: () => void;
}

export function useScheduleConflictFlow(): ScheduleConflictFlow {
  const [conflicts, setConflicts] = useState<ScheduleConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<ScheduleConflictItem[] | null>(null);
  const [violations, setViolations] = useState<ScheduleWorkingHoursViolation[] | null>(null);

  // What the rejected attempt sent, so confirming a dialog resends the identical payload —
  // uploads included — instead of uploading the same files a second time.
  const pendingRef = useRef<{
    attempt: ScheduleRetryAttempt;
    overrides: ScheduleOverrides;
  } | null>(null);

  const handle = useCallback<ScheduleConflictFlow["handle"]>((error, retry) => {
    const rejection = parseScheduleRejection(error);
    if (!rejection) return false;

    if (rejection.kind === "blockers") {
      // No retry is stashed: completed work in the range is not overridable, so there is nothing
      // to resend and offering it would be a lie.
      pendingRef.current = null;
      setBlockers(rejection.items);
      return true;
    }

    pendingRef.current = retry;
    if (rejection.kind === "workingHours") setViolations(rejection.items);
    else setConflicts(rejection.items);
    return true;
  }, []);

  /**
   * Overrides **accumulate** rather than replace: the API validates working hours before overlaps,
   * so a slot that breaks both rules answers 409 twice in a row, and the second confirmation must
   * keep the first override or the retry just reruns the check the user already answered.
   */
  const retryWith = useCallback(async (override: ScheduleOverrides) => {
    const pending = pendingRef.current;
    if (!pending) return;
    // Cleared before the attempt runs, so a second rejection stashes fresh state rather than
    // racing with the one it replaces.
    pendingRef.current = null;
    await pending.attempt({ ...pending.overrides, ...override });
  }, []);

  const confirmConflict = useCallback(async () => {
    setConflicts(null);
    await retryWith({ force: true });
  }, [retryWith]);

  const confirmWorkingHours = useCallback(async () => {
    setViolations(null);
    await retryWith({ allowOutsideWorkingHours: true });
  }, [retryWith]);

  const dismiss = useCallback(() => {
    setConflicts(null);
    setViolations(null);
    pendingRef.current = null;
  }, []);

  const closeBlockers = useCallback(() => setBlockers(null), []);

  const reset = useCallback(() => {
    setConflicts(null);
    setBlockers(null);
    setViolations(null);
    pendingRef.current = null;
  }, []);

  return {
    conflicts,
    blockers,
    violations,
    handle,
    confirmConflict,
    confirmWorkingHours,
    dismiss,
    closeBlockers,
    reset,
  };
}
